import React, { FC, useCallback, useMemo, useState } from "react";
import { LayoutChangeEvent, View } from "react-native";
import {
  Bullets,
  Carousel,
  LazyImage,
  RenderBulletsFunction,
  RenderItemFunction,
  useScreenSize,
} from "@lookiero/sty-psp-ui";
import { MediaPerspective, MediaProjection } from "../../../../../../projection/checkoutItem/checkoutItem";
import { useMediaImage } from "../../../../hooks/useMediaImage";
import { style as productVariantSliderStyle } from "./ProductVariantSlider.style";
import { AspectRatioView } from "./components/aspectRatioView/AspectRatioView";

interface ProductVariantSlider {
  readonly producVariantMedia: MediaProjection[];
  readonly onChanged?: (active: number) => void;
  readonly availableHeight: number;
}

const ProductVariantSlider: FC<ProductVariantSlider> = ({ producVariantMedia, onChanged, availableHeight }) => {
  const cdnImageUrl = useMediaImage();
  const screenSize = useScreenSize();
  const isDesktopScreen = screenSize !== "S";

  const style = useMemo(() => productVariantSliderStyle(), []);

  const [activeIndex, setActiveIndex] = useState(0);
  const handleOnActiveChanged = useCallback(
    (activeIndex: number) => {
      setActiveIndex(activeIndex);
      onChanged?.(activeIndex);
    },
    [onChanged],
  );

  const [width, setWidth] = useState<number>(0);
  const handleOnLayout = useCallback(
    ({
      nativeEvent: {
        layout: { width },
      },
    }: LayoutChangeEvent) => setWidth(width),
    [],
  );

  const aspectRatio = Math.min(4 / 3, availableHeight / width);

  const renderItem: RenderItemFunction<MediaProjection> = useCallback(
    ({ item, index }) => {
      const isCollageImage = item.perspective === MediaPerspective.COLLAGE;
      const isLastItem = index === producVariantMedia.length - 1;

      return (
        <View onLayout={handleOnLayout}>
          <AspectRatioView
            aspectRatio={aspectRatio}
            multiplier={isCollageImage && isLastItem ? 2 : 1}
            style={{
              alignSelf: isCollageImage ? (isLastItem ? "flex-end" : "flex-start") : undefined,
            }}
          >
            <LazyImage
              hiResSrc={cdnImageUrl({ url: item.url, width: 600 })}
              resizeMode={isCollageImage ? "stretch" : "contain"}
              src={cdnImageUrl({ url: item.url, width: 600, dpi: 1 })}
              style={{ view: [style.image, isDesktopScreen ? style.largeImage : null] }}
              testID="product-variant-image"
            />
          </AspectRatioView>
        </View>
      );
    },
    [
      aspectRatio,
      cdnImageUrl,
      handleOnLayout,
      isDesktopScreen,
      producVariantMedia.length,
      style.image,
      style.largeImage,
    ],
  );

  const renderBullets: RenderBulletsFunction = useCallback(
    ({ activeIndex, count, onChange }) => <Bullets activeIndex={activeIndex} count={count} onChange={onChange} />,
    [],
  );

  return (
    <Carousel
      activeIndex={activeIndex}
      bullets={renderBullets}
      data={producVariantMedia}
      style={{ bullets: style.bullets }}
      onActiveIndexChanged={handleOnActiveChanged}
    >
      {renderItem}
    </Carousel>
  );
};

export { ProductVariantSlider };
